import { useNavigate, useLocation } from "react-router-dom";
import { useTranslation } from "react-i18next";
import { useDispatch, useSelector } from "react-redux";
import { motion, AnimatePresence } from "framer-motion";
import React, { useEffect, useState, useMemo, useRef } from "react";
import { Sun, Moon, User, Lock, LogOut, ChevronDown } from "lucide-react";

import type { AppDispatch, RootState } from "../../store";
import { toggleDarkMode } from "../../store/theme/themeSlice";
import { changeLanguage } from "../../store/language/languageSlice";
import { logout } from "../../store/auth/authSlice";
import { privateRequest } from "../../utils/api";

const AdminHeader: React.FC = () => {
  const navigate = useNavigate();
  const location = useLocation();
  const { i18n } = useTranslation();
  const dispatch = useDispatch<AppDispatch>();
  const [isLanguageMenuOpen, setIsLanguageMenuOpen] = useState(false);
  const [isUserMenuOpen, setIsUserMenuOpen] = useState(false);
  const userMenuRef = useRef<HTMLDivElement>(null);
  const { darkMode } = useSelector((state: RootState) => state.theme);
  const { currentLanguage } = useSelector((state: RootState) => state.language);
  const { user } = useSelector((state: RootState) => state.auth);

  useEffect(() => {
    if (darkMode) {
      document.documentElement.classList.add("dark");
    } else {
      document.documentElement.classList.remove("dark");
    }
  }, [darkMode]);

  useEffect(() => {
    i18n.changeLanguage(currentLanguage);
  }, [currentLanguage, i18n]);

  // Close user menu when clicking outside
  useEffect(() => {
    const handleClickOutside = (event: MouseEvent) => {
      if (
        userMenuRef.current &&
        !userMenuRef.current.contains(event.target as Node)
      ) {
        setIsUserMenuOpen(false);
      }
    };

    if (isUserMenuOpen) {
      document.addEventListener("mousedown", handleClickOutside);
    }

    return () => {
      document.removeEventListener("mousedown", handleClickOutside);
    };
  }, [isUserMenuOpen]);

  // When language changes, update URL
  const handleLanguageChange = (newLang: string) => {
    dispatch(changeLanguage(newLang));
    const { pathname, search } = location;
    const newPath = pathname.replace(/^\/[a-z]{2}/, `/${newLang}`);
    navigate(`${newPath}${search}`, { replace: true });
    setIsLanguageMenuOpen(false);
  };

  const handleThemeToggle = () => {
    dispatch(toggleDarkMode());
  };

  const handleLogout = async () => {
    try {
      await privateRequest.post("/auth/logout");
      dispatch(logout());
      navigate(`/${currentLanguage}/auth/login`);
    } catch (error) {
      console.error("Logout failed:", error);
      // Still logout locally even if API call fails
      dispatch(logout());
      navigate(`/${currentLanguage}/auth/login`);
    }
    setIsUserMenuOpen(false);
  };

  const { t } = useTranslation();

  // Memoized data for better performance
  const languages = useMemo(
    () => [
      {
        code: "en",
        name: t("header.language") === "Language" ? "English" : "English",
        flag: "🇺🇸",
      },
      {
        code: "fr",
        name: t("header.language") === "Language" ? "Français" : "Français",
        flag: "🇫🇷",
      },
    ],
    [t]
  );

  return (
    <motion.header
      initial={{ y: -100 }}
      animate={{ y: 0 }}
      transition={{ duration: 0.5 }}
      className="sticky top-0 z-20 bg-white dark:bg-gray-900 shadow-sm border-b border-gray-200 dark:border-gray-600"
    >
      <div className="max-w-full mx-auto px-4 lg:px-6">
        <div className="flex items-center justify-between h-16">
          {/* Left Section - Admin Logo */}
          <div
            className="flex items-center gap-4 hover:cursor-pointer"
            onClick={() => (window.location.href = `/${currentLanguage}`)}
          >
            <svg
              viewBox="0 0 48 48"
              fill="none"
              xmlns="http://www.w3.org/2000/svg"
              className="size-6 text-blue-400"
            >
              <path
                fillRule="evenodd"
                clipRule="evenodd"
                d="M24 4H6V17.3333V30.6667H24V44H42V30.6667V17.3333H24V4Z"
                fill="currentColor"
              />
            </svg>
            <h2 className="text-lg md:text-xl font-bold text-gray-900 dark:text-white">
              {t("admin.title")}
            </h2>
          </div>

          {/* Right Section - Controls */}
          <div className="flex items-center gap-2 md:gap-4">
            {/* Language Dropdown */}
            <div className="relative">
              <button
                id="languageButton"
                onClick={() => {
                  setIsLanguageMenuOpen(!isLanguageMenuOpen);
                  setIsUserMenuOpen(false);
                }}
                className="p-2 flex items-center gap-2 bg-gray-100 dark:bg-gray-700 text-gray-700 dark:text-gray-300 hover:bg-gray-200 dark:hover:bg-gray-600 transition-colors duration-200 rounded-lg"
                aria-haspopup="true"
                aria-expanded={isLanguageMenuOpen}
              >
                <span>
                  {languages.find((lang) => lang.code === currentLanguage)
                    ?.flag || "🌐"}
                </span>
                <span className="hidden lg:inline text-sm lg:text-base">
                  {languages.find((lang) => lang.code === currentLanguage)
                    ?.name || t("header.language")}
                </span>
              </button>

              <AnimatePresence>
                {isLanguageMenuOpen && (
                  <motion.div
                    initial={{ opacity: 0, scale: 0.95, y: -10 }}
                    animate={{ opacity: 1, scale: 1, y: 0 }}
                    exit={{ opacity: 0, scale: 0.95, y: -10 }}
                    transition={{ duration: 0.2 }}
                    className="absolute right-0 mt-2 w-48 bg-white dark:bg-gray-800 rounded-lg shadow-lg ring-1 ring-gray-200 dark:ring-gray-600 focus:outline-none z-50 overflow-hidden"
                  >
                    {languages.map((lang) => (
                      <button
                        key={lang.code}
                        onClick={() => handleLanguageChange(lang.code)}
                        className="block w-full text-left px-4 py-2 text-sm hover:bg-gray-100 dark:hover:bg-gray-700 text-gray-700 dark:text-gray-300 items-center gap-2 transition-colors"
                      >
                        <span className="mr-2">{lang.flag}</span>
                        <span>{lang.name}</span>
                      </button>
                    ))}
                  </motion.div>
                )}
              </AnimatePresence>
            </div>

            {/* Theme Toggle */}
            <button
              onClick={handleThemeToggle}
              className="p-2 bg-gray-100 dark:bg-gray-700 text-gray-700 dark:text-gray-300 hover:bg-gray-200 dark:hover:bg-gray-600 transition-colors duration-200 rounded-lg"
              aria-label={
                darkMode ? "Switch to light mode" : "Switch to dark mode"
              }
            >
              {darkMode ? <Sun size={20} /> : <Moon size={20} />}
            </button>

            {/* User Dropdown */}
            {user && (
              <div className="relative" ref={userMenuRef}>
                <button
                  onClick={() => {
                    setIsUserMenuOpen(!isUserMenuOpen);
                    setIsLanguageMenuOpen(false);
                  }}
                  className="flex items-center gap-2 px-3 py-2 bg-gray-100 dark:bg-gray-700 text-gray-700 dark:text-gray-300 hover:bg-gray-200 dark:hover:bg-gray-600 transition-colors duration-200 rounded-lg"
                  aria-haspopup="true"
                  aria-expanded={isUserMenuOpen}
                >
                  <div className="flex items-center gap-2">
                    <div className="w-8 h-8 rounded-full bg-gradient-to-br from-blue-500 to-cyan-500 flex items-center justify-center text-white font-semibold text-sm">
                      {user.name?.charAt(0).toUpperCase() || "U"}
                    </div>
                    <div className="hidden md:block text-left">
                      <div className="text-sm font-semibold text-gray-900 dark:text-white">
                        {user.name}
                      </div>
                      <div className="text-xs text-gray-500 dark:text-gray-400 truncate max-w-[120px]">
                        {user.email}
                      </div>
                    </div>
                  </div>
                  <ChevronDown
                    size={16}
                    className={`transition-transform duration-200 ${
                      isUserMenuOpen ? "rotate-180" : ""
                    }`}
                  />
                </button>

                <AnimatePresence>
                  {isUserMenuOpen && (
                    <motion.div
                      initial={{ opacity: 0, scale: 0.95, y: -10 }}
                      animate={{ opacity: 1, scale: 1, y: 0 }}
                      exit={{ opacity: 0, scale: 0.95, y: -10 }}
                      transition={{ duration: 0.2 }}
                      className="absolute right-0 mt-2 w-64 bg-white dark:bg-gray-800 rounded-lg shadow-xl ring-1 ring-gray-200 dark:ring-gray-600 focus:outline-none z-50 overflow-hidden"
                    >
                      {/* User Info Section */}
                      <div className="px-4 py-3 border-b border-gray-200 dark:border-gray-700 bg-gradient-to-r from-blue-50 to-cyan-50 dark:from-blue-900/20 dark:to-cyan-900/20">
                        <div className="flex items-center gap-3">
                          <div className="w-10 h-10 rounded-full bg-gradient-to-br from-blue-500 to-cyan-500 flex items-center justify-center text-white font-semibold text-base">
                            {user.name?.charAt(0).toUpperCase() || "U"}
                          </div>
                          <div className="flex-1 min-w-0">
                            <div className="text-sm font-semibold text-gray-900 dark:text-white truncate">
                              {user.name}
                            </div>
                            <div className="text-xs text-gray-600 dark:text-gray-400 truncate">
                              {user.email}
                            </div>
                          </div>
                        </div>
                      </div>

                      {/* Menu Items */}
                      <div className="py-1">
                        <button
                          onClick={() => {
                            navigate(`/${currentLanguage}/admin/profile`);
                            setIsUserMenuOpen(false);
                          }}
                          className="w-full flex items-center gap-3 px-4 py-2.5 text-sm text-gray-700 dark:text-gray-300 hover:bg-gray-100 dark:hover:bg-gray-700 transition-colors"
                        >
                          <User
                            size={18}
                            className="text-gray-500 dark:text-gray-400"
                          />
                          <span>{t("admin.header.profile", "Profile Settings")}</span>
                        </button>
                        <button
                          onClick={() => {
                            navigate(
                              `/${currentLanguage}/admin/change-password`
                            );
                            setIsUserMenuOpen(false);
                          }}
                          className="w-full flex items-center gap-3 px-4 py-2.5 text-sm text-gray-700 dark:text-gray-300 hover:bg-gray-100 dark:hover:bg-gray-700 transition-colors"
                        >
                          <Lock
                            size={18}
                            className="text-gray-500 dark:text-gray-400"
                          />
                          <span>
                            {t("admin.header.change_password", "Change Password")}
                          </span>
                        </button>
                        <div className="border-t border-gray-200 dark:border-gray-700 my-1"></div>
                        <button
                          onClick={handleLogout}
                          className="w-full flex items-center gap-3 px-4 py-2.5 text-sm text-red-600 dark:text-red-400 hover:bg-red-50 dark:hover:bg-red-900/20 transition-colors"
                        >
                          <LogOut size={18} />
                          <span>{t("admin.header.logout", "Logout")}</span>
                        </button>
                      </div>
                    </motion.div>
                  )}
                </AnimatePresence>
              </div>
            )}
          </div>
        </div>
      </div>
    </motion.header>
  );
};

// Display name for debugging
AdminHeader.displayName = "AdminHeader";

export default AdminHeader;
